import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { bemGenerator } from '@prisjakt/ui-base';

import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';

import Selectable from './components/selectable';
import ShowAllResults from './components/show-all';
import SearchItem from './components/item';

import './render.scss';

const styles = bemGenerator('pj-ui-search', [
  'categories',
  'no-results',
  'show-all',
  'products',
  'product-item',
]);

const triggerSelection = () => {
  const a = document.querySelector('*[data-selected="true"] a');

  a.click();
};

class SearchPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selected: undefined,
    };

    this.spy = this.spy.bind(this);
  }

  componentDidMount() {
    window.addEventListener('keydown', this.spy);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.spy);
  }

  handleEnter() {
    const { push } = this.props.history;

    const { activeElement } = document;
    const selected = document.querySelector('*[data-selected="true"]');

    if (selected) {
      triggerSelection();
    } else if (activeElement && activeElement.tagName === 'INPUT') {
      const query = activeElement.value.trim();

      if (query.length === 0) {
        return;
      }

      push(`/search?q=${encodeURIComponent(query)}`);
    }
  }

  handleSideMove() {
    const { activeElement } = document;
    const { push } = this.props.history;

    if (activeElement.tagName !== 'INPUT') {
      return;
    }

    const query = this.props.query;
    const { value } = activeElement;

    if (query !== value) {
      push(`/search?q=${encodeURIComponent(value)}`);
    }
  }

  changeSearchInput(selected) {
    const { activeElement } = document;
    if (activeElement.tagName !== 'INPUT') {
      return;
    }
    const currentValue = this.props.query;

    if (!selected || !selected.query) {
      activeElement.value = currentValue;
    } else {
      const newValue = `${selected.query} `;

      activeElement.value = newValue;
      activeElement.setSelectionRange(currentValue.length, newValue.length);
    }
  }

  handleKeyMove(direction, e) {
    e.preventDefault();

    const selectable = [...document.querySelectorAll('*[data-selected]')].map(
      n => ({
        id: n.dataset.selectionId,
        query: n.dataset.query,
        selected: n.dataset.selected === 'true',
      })
    );

    if (selectable.length === 0) {
      return;
    }

    const selected = selectable.findIndex(n => n.selected);
    let newIndex;

    if (direction === 'down') {
      newIndex = selected + 1 === selectable.length ? -1 : selected + 1;
    }

    if (direction === 'up') {
      newIndex = selected >= 0 ? selected - 1 : selectable.length - 1;
    }

    if (newIndex === -1) {
      this.setState({ selected: false });
    } else {
      this.setState({ selected: selectable[newIndex].id });
    }

    this.changeSearchInput(selectable[newIndex]);
  }

  spy(e) {
    if (!this.props.keyboardNavigation) {
      return;
    }

    switch (e.keyCode) {
      case 40: {
        this.handleKeyMove('down', e);
        break;
      }
      case 38: {
        this.handleKeyMove('up', e);
        break;
      }
      case 13: {
        this.handleEnter();
        break;
      }
      case 37: {
        this.handleSideMove('left');
        break;
      }
      case 39: {
        this.handleSideMove('right');
        break;
      }

      default:
    }
  }

  render() {
    const {
      query,
      onEnterSearchMenu,
      onExitSearchMenu,
      onRawClick,
      onItemClick,
      onSelect,
      results,
      showAllResults,
      styling,
    } = this.props;
    const { t } = this.context;
    const { selected } = this.state;

    return (
      <div
        className={styles.block}
        onMouseEnter={onEnterSearchMenu}
        onMouseLeave={onExitSearchMenu}
        onTouchStart={onEnterSearchMenu}
        onTouchEnd={onExitSearchMenu}
      >
        <div>
          {styling === 'rich' && showAllResults && (
            <div
              className={styles['show-all'].withModifier({ [styling]: true })}
            >
              <Selectable id={-1} selected={selected}>
                <ShowAllResults query={query} styling={styling} />
              </Selectable>
            </div>
          )}

          {results.map(group => (
            <div key={group.name}>
              {group.name === 'products' && group.hits.length > 0 && (
                <Panel
                  margin={styling === 'rich' ? 'bottom' : 'none'}
                  header={
                    styling === 'rich' && (
                      <PanelHeader
                        title={t('search', 'relevant-products.title', {
                          query,
                        })}
                      />
                    )
                  }
                >
                  <div
                    className={styles.products.withModifier({
                      [styling]: true,
                    })}
                    role="listbox"
                  >
                    {group.hits.map(product => (
                      <Selectable
                        key={product.selectionId}
                        id={product.selectionId}
                        selected={selected}
                      >
                        <div className={styles['product-item']}>
                          <SearchItem
                            type={product.type}
                            item={product.item}
                            onClick={onItemClick}
                            onRawClick={onRawClick}
                            styling={styling}
                            query={query}
                            onSelect={onSelect}
                          />
                        </div>
                      </Selectable>
                    ))}
                  </div>
                </Panel>
              )}
              {group.name === 'categories' && group.hits.length > 0 && (
                <Panel
                  margin={styling === 'rich' ? 'bottom' : 'none'}
                  header={
                    styling === 'rich' && (
                      <PanelHeader
                        title={t('search', 'relevant-categories.title', {
                          query,
                        })}
                      />
                    )
                  }
                >
                  <div
                    role="listbox"
                    className={styles.categories.withModifier({
                      [styling]: true,
                    })}
                  >
                    {group.hits.map(category => (
                      <Selectable
                        key={category.selectionId}
                        id={category.selectionId}
                        selected={selected}
                      >
                        <SearchItem
                          type={category.type}
                          item={category.item}
                          styling={styling}
                          onClick={onItemClick}
                          onRawClick={onRawClick}
                          callToActionText={
                            styling === 'simple'
                              ? t(
                                  'search',
                                  'quick-search.category-call-to-action'
                                )
                              : null
                          }
                          mode="row"
                          query={query}
                          onSelect={onSelect}
                        />
                      </Selectable>
                    ))}
                  </div>
                </Panel>
              )}
            </div>
          ))}
          {styling === 'simple' && showAllResults && (
            <Selectable
              id={-1}
              selected={selected}
              className={styles['show-all'].withModifier({ [styling]: true })}
            >
              <ShowAllResults query={query} styling={styling} />
            </Selectable>
          )}
        </div>
      </div>
    );
  }
}

SearchPage.propTypes = {
  query: PropTypes.string.isRequired,
  results: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  onSelect: PropTypes.func,
  onEnterSearchMenu: PropTypes.func,
  onExitSearchMenu: PropTypes.func,
  showAllResults: PropTypes.bool,
  keyboardNavigation: PropTypes.bool,
  styling: PropTypes.oneOf(['simple', 'rich']),
  onRawClick: PropTypes.func,
  onItemClick: PropTypes.func,
  history: PropTypes.object.isRequired,
};

SearchPage.defaultProps = {
  onSelect: undefined,
  onEnterSearchMenu: () => {},
  onExitSearchMenu: () => {},
  showAllResults: false,
  keyboardNavigation: false,
  styling: 'rich',
  onRawClick: () => {},
  onItemClick: () => {},
};

export default withRouter(SearchPage);

SearchPage.contextType = AppContext;
